<template>
  <div class="home-echarts-wrap" :style="{ width, height,marginBottom }">
    <div class="home-echarts-header" style="padding: 10px 24px 0 24px;height: auto">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
          <a-date-picker
                  v-model:value="monthValue"
                  :allowClear="false"
                  v-if="queryForm.dateQueryType===1"
                  @change="handleDateChange"
                  picker="month"
          />
        <a-range-picker v-model:value="weekValue" :allowClear="false" v-if="queryForm.dateQueryType===2" picker="week" @change="handleDateChange"/>
          <a-date-picker
                  @change="handleDateChange"
                  v-model:value="yearValue"
                  :allowClear="false"
                  v-if="queryForm.dateQueryType===3"
                  picker="year"
          />
        <a-radio-group v-model:value="queryForm.dateQueryType" style="margin-left: 10px;" class="ant-radio-group" @change="handleDateChange">
          <a-radio-button :value="1"> 天 </a-radio-button>
          <a-radio-button :value="2"> 周 </a-radio-button>
          <a-radio-button :value="3"> 月 </a-radio-button>
        </a-radio-group>
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import type { PropType, Ref } from 'vue'
import { onBeforeMount, reactive, ref, watch } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import { getMonthDays } from '@/utils/timeFormat'
import dayjs, { Dayjs } from 'dayjs'
import { getFollowUpRate } from '@/api/gucangSales/regionDataBoard'

interface QueryFormType {
  customerRegionEmployeeNoList: string[]
  startDay?: string
  endDay?: string
  startWeek?: string
  endWeek?: string
  startMonth?: string
  endMonth?: string
  // 1/2/3,按天/按周/按月】
  dateQueryType: number,
}
interface ItemCommonType {
  name?: string
  quantity: number,
  total: number,
  prop?: string,
  monthValue?:number,
  weekValue?:number,
  dayValue?:string|number
}
const yearValue = ref<Dayjs>(dayjs())
const monthValue = ref<Dayjs>(dayjs())
const weekValue = ref<[Dayjs, Dayjs]>([dayjs().subtract(13, 'week'), dayjs()])
const props = defineProps({
  title: {
    type: String as PropType<string>,
    default: '跟进及时率'
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_lbu'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '350px'
  },
  marginBottom: {
    type: String as PropType<string>,
    default: '8px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  customerRegionEmployeeNoList: {
    type: Array as PropType<string[]>,
    default: () => []
  }
})
const spinning = ref(false)
const showECharts = ref(false)
const xAxisData = ref<any[]>([])
const { firstDayOfMonth, lastDayOfMonth } = getMonthDays(undefined, 'YYYY/MM/DD')
const queryForm = reactive<QueryFormType>({
  startDay: firstDayOfMonth,
  endDay: lastDayOfMonth,
  customerRegionEmployeeNoList: props.customerRegionEmployeeNoList,
  dateQueryType: 1
})
const handleDateChange = () => {
  if (queryForm.dateQueryType === 3) {
    queryForm.startDay = undefined
    queryForm.endDay = undefined
    queryForm.startWeek = undefined
    queryForm.endWeek = undefined
    queryForm.startMonth = dayjs(yearValue.value).startOf('month').format('YYYY01')
    queryForm.endMonth = dayjs(yearValue.value).endOf('month').format('YYYY12')
    xAxisData.value = generateTimeArray(queryForm.startMonth, queryForm.endMonth, 'YYYYmm')
  } else if (queryForm.dateQueryType === 2) {
    queryForm.startDay = undefined
    queryForm.endDay = undefined
    queryForm.startMonth = undefined
    queryForm.endMonth = undefined
    queryForm.startWeek = dayjs(weekValue.value[0]).format('YYYYww')
    queryForm.endWeek = dayjs(weekValue.value[1]).format('YYYYww')
    xAxisData.value = generateTimeArray(queryForm.startWeek, queryForm.endWeek, 'YYYYww')
  } else if (queryForm.dateQueryType === 1) {
    queryForm.startWeek = undefined
    queryForm.endWeek = undefined
    queryForm.startMonth = undefined
    queryForm.endMonth = undefined
    queryForm.startDay = getMonthDays(monthValue.value, 'YYYY/MM/DD').firstDayOfMonth
    queryForm.endDay = getMonthDays(monthValue.value, 'YYYY/MM/DD').lastDayOfMonth
    xAxisData.value = generateTimeArray(queryForm.startDay, queryForm.endDay, 'YYYY/MM/DD')
  }
  getData()
}
function generateTimeArray(startTime: string, endTime: string, format: 'YYYYmm'|'YYYYww'|'YYYY/MM/DD') {
  const start = dayjs(startTime, format)
  const end = dayjs(endTime, format)
  const result = []

  let current = start

  if (format === 'YYYYmm') {
    while (current.isBefore(end) || current.isSame(end)) {
      result.push({ value: current.format(format), name: current.format('m') + '月' })
      current = current.add(1, 'm')
    }
  } else if (format === 'YYYYww') {
    return getWeeksBetween(startTime, endTime)
  } else if (format === 'YYYY/MM/DD') {
    while (current.isBefore(end) || current.isSame(end)) {
      result.push({ value: current.format(format), name: current.format('D') })
      current = current.add(1, 'day')
    }
  } else {
    throw new Error('Unsupported format')
  }
  return result
}
function getWeeksBetween(startTime: string, endTime: string) {
  const weeks = []
  let [startYear, startWeek] = [parseInt(startTime.slice(0, 4)), parseInt(startTime.slice(4, 6))]
  const [endYear, endWeek] = [parseInt(endTime.slice(0, 4)), parseInt(endTime.slice(4, 6))]

  while (startYear < endYear || (startYear === endYear && startWeek <= endWeek)) {
    weeks.push({ value: `${startYear}${String(startWeek).padStart(2, '0')}`, name: `${startYear}-${String(startWeek)}周` })
    startWeek++
    if (startWeek > 52) { // 假设一年最多52周
      startWeek = 1
      startYear++
    }
  }
  return weeks
}
watch(() => props.customerRegionEmployeeNoList, (val) => {
  queryForm.customerRegionEmployeeNoList = val
  getData()
})
const getData = () => {
  spinning.value = true
  getFollowUpRate(queryForm)
    .then((res) => {
      const potentialFollowingUpRateList = res.data?.potentialFollowingUpRateList as unknown as ItemCommonType[]
      const contractFollowingUpRateList = res.data?.contractFollowingUpRateList as unknown as ItemCommonType[]
      const transactionFollowingUpRateList = res.data?.transactionFollowingUpRateList as unknown as ItemCommonType[]
      const theChartPotentialData = transChartData(potentialFollowingUpRateList)
      const theChartContractData = transChartData(contractFollowingUpRateList)
      const theChartTransactionData = transChartData(transactionFollowingUpRateList)
      if ((theChartPotentialData.length === 0) &&
        (theChartContractData.length === 0) &&
        (theChartTransactionData.length === 0)) {
        showECharts.value = false
        initChart([], [], [])
      } else {
        showECharts.value = true
        initChart(theChartPotentialData, theChartContractData, theChartTransactionData)
      }
    })
    .finally(() => {
      spinning.value = false
    })
}
/// 格式化接口数据和日期对应
const transChartData = (serviceData: ItemCommonType[]) => {
  const matchData: ItemCommonType[] = []
  if (serviceData && serviceData.length > 0) {
    xAxisData.value.forEach((xAxis) => {
      const matchItem = serviceData.find((item) => {
        if (queryForm.dateQueryType === 1) {
          return item.dayValue === xAxis.value
        } else if (queryForm.dateQueryType === 2) {
          return (item.weekValue ?? 0).toString() === xAxis.value
        } else if (queryForm.dateQueryType === 3) {
          return (item.monthValue ?? 0).toString() === xAxis.value
        } else {
          return false
        }
      })
      if (matchItem) {
        matchData.push(matchItem)
      } else {
        matchData.push({ quantity: 0, total: 0 })
      }
    })
  }
  return matchData
}
const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>, undefined, false)
const initChart = (potentialFollowingUpRateList: ItemCommonType[],
  contractFollowingUpRateList: ItemCommonType[],
  transactionFollowingUpRateList: ItemCommonType[]) => {
  setOptions({
    // 悬浮窗
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1D4CD2',
      textStyle: {
        color: '#fff'
      },
      formatter: function (params) {
        const currentAxis = xAxisData.value[params.dataIndex].value
        let currentHoverData
        if (params.seriesIndex === 0) {
          currentHoverData = potentialFollowingUpRateList[params.dataIndex]
        } else if (params.seriesIndex === 1) {
          currentHoverData = contractFollowingUpRateList[params.dataIndex]
        } else if (params.seriesIndex === 2) {
          currentHoverData = transactionFollowingUpRateList[params.dataIndex]
        }
        const toolsTipsResult = []
        toolsTipsResult.push(currentAxis)
        toolsTipsResult.push('及时客户  <strong>' + currentHoverData?.quantity + '</strong>')
        toolsTipsResult.push('总客户  <strong>' + currentHoverData?.total + '</strong>')
        toolsTipsResult.push(`及时率  <strong>${(currentHoverData?.total ? Number((((currentHoverData.quantity ?? 0) / currentHoverData.total) * 100).toFixed(2)) : 0)}%` + '</strong>')
        return toolsTipsResult.join('<br>')
      }
    },
    /// 在这里配置线条颜色和legend颜色一致
    color: ['#FF9A3E', '#1CA550', '#1D4CD2'],
    legend: {
      itemWidth: 12,
      itemHeight: 2,
      left: 'right',
      icon: 'react',
      right: '50%',
      data: ['潜在', '签约', '成交']
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData.value.map((item) => item.name)
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} %'
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#D8DFE4',
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: '潜在',
        type: 'line',
        smooth: true,
        data: potentialFollowingUpRateList.map((item) => item.total ? Number((((item.quantity ?? 0) / item.total) * 100).toFixed(2)) : 0),
        emphasis: {
          focus: 'series'
        },
        // lineStyle: {
        //   color: 'rgb(245, 101, 101)'
        //   // // 设置线条的style等
        //   // normal: {
        //   //   color: 'rgb(60,179,113)'
        //   // }
        // },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              // 渐变颜色
              {
                offset: 0,
                color: 'rgb(255,154,62,0.5)'
              },
              {
                offset: 1,
                color: 'rgb(255,154,62,0.0)'
              }
            ],
            global: false
          }
        }
      },
      {
        name: '签约',
        type: 'line',
        smooth: true,
        data: contractFollowingUpRateList.map((item) => item.total ? Number((((item.quantity ?? 0) / item.total) * 100).toFixed(2)) : 0),
        emphasis: {
          focus: 'series'
        },
        // lineStyle: {
        //   color: 'rgb(56, 161, 105, 1)'
        //   // // 设置线条的style等
        //   // normal: {
        //   //   color: 'rgb(255,99,71)'
        //   // }
        // },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              // 渐变颜色
              {
                offset: 0,
                color: 'rgb(56, 161, 105,0.5)'
              },
              {
                offset: 1,
                color: 'rgb(56, 161, 105,0.0)'
              }
            ],
            global: false
          }
        }
      },
      {
        name: '成交',
        type: 'line',
        smooth: true,
        data: transactionFollowingUpRateList.map((item) => item.total ? Number((((item.quantity ?? 0) / item.total) * 100).toFixed(2)) : 0),
        emphasis: {
          focus: 'series'
        },
        // lineStyle: {
        //   color: 'rgb(29, 76, 210)'
        //   // // 设置线条的style等
        //   // normal: {
        //   //   color: 'rgb(65,105,225)'
        //   // }
        // },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              // 渐变颜色
              {
                offset: 0,
                color: 'rgb(29, 76, 210,0.5)'
              },
              {
                offset: 1,
                color: 'rgb(29, 76, 210,0.0)'
              }
            ],
            global: false
          }
        }
      }
    ]
  })
}
onBeforeMount(() => {
  if (!queryForm.startDay) {
    queryForm.startDay = getMonthDays(undefined, 'YYYY/MM/DD').firstDayOfMonth
  }
  if (!queryForm.endDay) {
    queryForm.endDay = getMonthDays(undefined, 'YYYY/MM/DD').lastDayOfMonth
  }
  xAxisData.value = generateTimeArray(queryForm.startDay, queryForm.endDay, 'YYYY/MM/DD')
  getData()
})
</script>
<script lang="ts">
export default { name: 'FollowInTimeRate' }
</script>

<style lang="scss" scoped>
.ant-radio-group {
  display: flex;
  flex-wrap: nowrap; /* 设置为不换行 */
}
</style>
